//Temporarily in this file until we have a shared components library

import { ReactNode, useMemo, useState, useEffect, useRef } from 'react';
import { Flex, Select, Text, TextField } from '@radix-ui/themes';
import { MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { CountryItem, AudiencePlatformItem, AudienceCategoryItem } from '@digiseg-labs/api-client';
import { CountryFlag } from '@digiseg-labs/design-system';

export interface SelectableItem {
  code: string;
  display_name: string;
}

export interface CustomSelectItem {
  value: string;
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
}

export type SelectItemType = 
  | CountryItem 
  | AudiencePlatformItem 
  | AudienceCategoryItem 
  | SelectableItem 
  | CustomSelectItem;

const isCustomItem = (item: SelectItemType): item is CustomSelectItem => 
  'value' in item && 'label' in item;

const isCountryItem = (item: SelectItemType): item is CountryItem => 
  'code' in item && 'display_name' in item && 'reach_stats' in item;

export interface GenericSelectProps<T extends SelectItemType> {
  items: T[];
  selectedItem?: T;
  onItemChange: (item: T | undefined) => void;
  placeholder?: string;
  size?: '1' | '2' | '3';
  fullWidth?: boolean;
  withSearch?: boolean;
  searchPlaceholder?: string;
  searchMinLength?: number;
  groupBy?: (item: T) => string | undefined;
  renderIcon?: (item: T) => ReactNode;
  renderLabel?: (item: T) => string;
  renderCustomDisplay?: (item: T) => ReactNode;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  loadingText?: string;
  sectionLabel?: string;
  emptyOption?: {
    label?: string;
    enabled?: boolean;
    icon?: ReactNode;
  };
  modalTrigger?: {
    label?: string;
    enabled?: boolean;
    onTrigger?: () => void;
    icon?: ReactNode;
  };
  otherOptions?: {
    label?: string;
    value?: string;
    items?: SelectableItem[];
    position?: 'top' | 'bottom';
    enabled?: boolean;
  };
}

const getItemCode = (item: SelectItemType): string => {
  return isCustomItem(item) ? item.value : item.code;
};

const getItemLabel = (item: SelectItemType): string => {
  return isCustomItem(item) ? item.label : item.display_name;
};

const getItemIcon = (item: SelectItemType): ReactNode => {
  if (isCustomItem(item)) {
    return item.icon;
  }
  
  if (isCountryItem(item)) {
    return <CountryFlag code={item.code} size={22} shape="square" />;
  }
  
  return undefined;
};

const createSearchFilter = <T extends SelectItemType>(
  items: T[],
  query: string,
  minLength: number = 2
): T[] => {
  const normalized = query.trim().toLowerCase();
  
  if (normalized.length < minLength) {
    return items;
  }

  return items.filter((item) => {
    const label = getItemLabel(item).toLowerCase();
    const code = getItemCode(item).toLowerCase();
    
    return label.includes(normalized) || code.includes(normalized);
  });
};

const groupItems = <T extends SelectItemType>(
  items: T[],
  groupBy: (item: T) => string | undefined
): Record<string, T[]> => {
  const groups: Record<string, T[]> = {};
  
  items.forEach((item) => {
    const group = groupBy(item) || 'Other';
    if (!groups[group]) {
      groups[group] = [];
    }
    groups[group].push(item);
  });
  
  return groups;
};

function GenericSelect<T extends SelectItemType>({
  items,
  selectedItem,
  onItemChange,
  placeholder = 'Select Platform',
  size = '3',
  fullWidth = false,
  withSearch = false,
  searchPlaceholder = 'Search...',
  searchMinLength = 2,
  groupBy,
  renderIcon,
  renderLabel,
  renderCustomDisplay,
  className,
  disabled = false,
  loading = false,
  loadingText = 'Loading...',
  sectionLabel,
  emptyOption = { label: '(None)', enabled: true },
  modalTrigger = { label: 'Country Selector', enabled: false },
  otherOptions = { label: 'Other', value: 'other', items: [], position: 'bottom', enabled: false },
}: GenericSelectProps<T>) {
  const [searchQuery, setSearchQuery] = useState('');
  const [internalSelectedValue, setInternalSelectedValue] = useState<string>('');
  const [open, setOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (withSearch && open && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [withSearch, open]);

  useEffect(() => {
    if (withSearch && open && searchInputRef.current && searchQuery) {
      const timeoutId = setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [searchQuery, withSearch, open]);

  const filteredItems = useMemo(() => {
    if (!withSearch) return items;
    return createSearchFilter(items, searchQuery, searchMinLength);
  }, [items, searchQuery, withSearch, searchMinLength]);

  const groupedItems = useMemo(() => {
    if (!groupBy) return null;
    return groupItems(filteredItems, groupBy);
  }, [filteredItems, groupBy]);

  const handleValueChange = (value: string) => {
    setInternalSelectedValue(value);
    if (modalTrigger?.enabled && value === '__modal__') {
      modalTrigger.onTrigger?.();
      return;
    }
    if (otherOptions?.enabled && value === (otherOptions.value || 'other')) {
      const otherItem = {
        code: otherOptions.value || 'other',
        display_name: otherOptions.label || 'Other',
      } as T;
      onItemChange(otherItem);
      return;
    }
    const otherOptionMatch = (otherOptions?.items || []).find(item => item.code === value);
    if (otherOptionMatch) {
      onItemChange(otherOptionMatch as T);
      return;
    }
    const item = items.find(item => getItemCode(item) === value);
    onItemChange(item || undefined);
  };

  const selectedValue = selectedItem 
    ? getItemCode(selectedItem) 
    : internalSelectedValue;

  const getDisplayText = () => {
    if (selectedItem) {
      return renderLabel ? renderLabel(selectedItem) : getItemLabel(selectedItem);
    }
    if (otherOptions.enabled && internalSelectedValue === otherOptions.value) {
      return otherOptions.label;
    }
    
    if (emptyOption.enabled && internalSelectedValue === '-') {
      return emptyOption.label;
    }
    
    if (internalSelectedValue === '__modal__') {
      return modalTrigger.label;
    }

    if (internalSelectedValue === '') {
      return placeholder;
    }
    
    const matchingItem = items.find(item => getItemCode(item) === internalSelectedValue);
    if (matchingItem) {
      return renderLabel ? renderLabel(matchingItem) : getItemLabel(matchingItem);
    }
    
    return placeholder;
  };

  const triggerClasses = [
    'generic-select-trigger',
    fullWidth ? 'generic-select-trigger--full' : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const renderOtherItem = (item: SelectableItem) => {
    const code = item.code;
    const label = item.display_name;

    if (!code || code.trim() === '') {
      return null;
    }
  
    return (
      <Select.Item key={code} value={code}>
        <Flex align="center" gap="2" style={{ paddingLeft: '20px' }}>
          <Text>{label}</Text>
        </Flex>
      </Select.Item>
    );
  };

  const renderItem = (item: T) => {
    const code = getItemCode(item);
    const label = renderLabel ? renderLabel(item) : getItemLabel(item);
    const icon = renderIcon ? renderIcon(item) : getItemIcon(item);
    const isDisabled = isCustomItem(item) && item.disabled;

    if (!code || code.trim() === '') {
      return null;
    }
  
    return (
      <Select.Item 
        key={code} 
        value={code}
        disabled={isDisabled}
      >
        {renderCustomDisplay ? (
          renderCustomDisplay(item)
        ) : (
          <Flex align="center" gap="2">
            {icon}
            <Text>{label}</Text>
          </Flex>
        )}
      </Select.Item>
    );
  };

  if (loading) {
    return (
      <Select.Root size={size} value="" disabled>
        <Select.Trigger placeholder={loadingText} className={triggerClasses} />
      </Select.Root>
    );
  }

  return (
    <Select.Root 
      size={size} 
      value={selectedValue} 
      onValueChange={handleValueChange}
      open={open}
      onOpenChange={setOpen}
      disabled={disabled}
    >
      <Select.Trigger placeholder={placeholder} className={triggerClasses}>
        <Flex align="center" gap="2" justify="between" className="w-100">
          <Flex align="center" gap="2" className="min-width-0">
            {selectedItem && (renderIcon ? renderIcon(selectedItem) : getItemIcon(selectedItem))}
            <Text className="text-truncate">
              {getDisplayText()}
            </Text>
          </Flex>
        </Flex>
      </Select.Trigger>
      
      <Select.Content position="popper" className="select-scrollable">
        {withSearch && (
          <div style={{ padding: '8px' }}>
            <TextField.Root
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}

              size="2"
              className="fullwidth"
              ref={searchInputRef}
            >
              <TextField.Slot side="left">
                <MagnifyingGlassIcon />
              </TextField.Slot>
            </TextField.Root>
          </div>
        )}

        <Select.Group>
          {modalTrigger.enabled && (
            <>
              <Select.Item value="__modal__">
                <Flex align="center" gap="2">
                  {typeof modalTrigger.icon !== 'undefined' ? modalTrigger.icon : null}
                  <Text>{modalTrigger.label || 'Open selector'}</Text>
                </Flex>
              </Select.Item>
              <Select.Separator />
            </>
          )}
          {emptyOption.enabled && (
            <>
              <Select.Item value="-">
                <Flex align="center" gap="2">
                  {typeof emptyOption.icon !== 'undefined' ? emptyOption.icon : null}
                  <Text>{emptyOption.label}</Text>
                </Flex>
              </Select.Item>
              {(items.length > 0 || otherOptions.enabled) && <Select.Separator />}
            </>
          )}

          {otherOptions.enabled && otherOptions.position === 'top' && (
            <>
              <Select.Item value={otherOptions?.value || 'other'}>
                <Text>{otherOptions.label}</Text>
              </Select.Item>
              {(otherOptions.items?.length ?? 0) > 0 && (
                <>
                  {otherOptions.items?.map(renderOtherItem).filter(Boolean)}
                </>
              )}
              {items.length > 0 && <Select.Separator />}
            </>
          )}

          {sectionLabel && !groupedItems && (
            <Select.Label>{sectionLabel}</Select.Label>
          )}
          {groupedItems ? (
            Object.entries(groupedItems).map(([groupName, groupItems]) => (
              <div key={groupName}>
                {groupName !== 'Other' && <Select.Label>{groupName}</Select.Label>}
                {groupItems.map(renderItem).filter(Boolean)}
              </div>
            ))
          ) : (
            filteredItems.map(renderItem).filter(Boolean)
          )}

          {otherOptions.enabled && otherOptions.position === 'bottom' && (
            <>
              {items.length > 0 && <Select.Separator />}
              <Select.Item value={otherOptions.value || 'other'}>
                <Text>{otherOptions.label}</Text>
              </Select.Item>
              {(otherOptions?.items?.length ?? 0) > 0 && (
                <>
                  {otherOptions.items?.map(renderOtherItem).filter(Boolean)}
                </>
              )}
            </>
          )}

          {withSearch && 
           searchQuery.length >= searchMinLength && 
           filteredItems.length === 0 && (
            <div style={{ padding: '8px' }}>
              <Text color="gray" size="2">
                No results found for "{searchQuery}".
              </Text>
            </div>
          )}
        </Select.Group>
      </Select.Content>
    </Select.Root>
  );
}

export default GenericSelect;